import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Lockup } from '../brand/Lockup';
import { useSearchParams } from 'react-router-dom';
import { authApi, type SignInMethods } from '../api/auth';
import { ApiError } from '../api/client';
import { renderGoogleButton } from '../lib/google';
import { Button } from '../ui/Button';
import { TextField } from '../ui/Field';
import { BetaNotice } from '../ui/BetaNotice';
import { DISCLAIMER } from '../ui/Chrome';

type Mode = 'signin' | 'invite' | 'claim';

interface Props {
  mode?: Mode;
  /** Founding invite token from /start?invite=… */
  inviteToken?: string;
  /** Account-claim token from /claim?token=… */
  claimToken?: string;
  /**
   * `redirectPath` is set only by the code door, which is the only one that can know where the
   * caller was headed: `?next=` is sanitised into the token when the code is issued and comes back
   * on the verify. Google's flow carries no such thing, and it is `"/"` whenever nobody asked.
   */
  onSignedIn: (result: { inviteId?: string; redirectPath?: string }) => void;
}

const COPY: Record<Mode, { heading: string; body: string; button: 'signin_with' | 'continue_with' }> = {
  signin: {
    heading: 'Sign in',
    body: 'Use the account your unit has on file. There is no password to remember.',
    button: 'signin_with',
  },
  // Both of these named Google until TROOP-224, when each door grew an email half. They now name
  // the *address* instead, which is the thing that actually has to be right: an invitation is
  // bound to the one it was sent to, so arriving with a different account is the failure these
  // sentences exist to prevent, whichever way the caller proves it.
  invite: {
    heading: 'Set up your unit',
    body: 'Use the address your invitation was sent to. Whoever signs in here becomes the unit’s first admin.',
    button: 'continue_with',
  },
  claim: {
    heading: 'Finish setting up your account',
    body: 'Use the address this sign-up link was sent to. Parents: your own address for your own record, and your child’s for theirs.',
    button: 'continue_with',
  },
};

/**
 * What the code door says in each mode (TROOP-224).
 *
 * The hedge in `sent` is load-bearing and differs on purpose. In `signin` the server answers 202
 * for an address on no roster and sends nothing, so the screen may not claim a code went out — and
 * the hedge names the roster, which is the question being withheld. On the other two the caller
 * holds an invitation, so the fact being withheld is narrower: whether *this* address is the one it
 * was sent to. Saying so is also the most useful thing the screen can say, because using the wrong
 * address is the mistake this door actually produces.
 */
const CODE_COPY: Record<
  Mode,
  { or: string; hint: string; sent: string; submit: string }
> = {
  signin: {
    or: 'Or, if the address your unit has on file is not a Google account:',
    hint: 'We send a code that signs you in. Nothing to remember.',
    sent:
      'If that address is on a unit roster, a six-digit code is on its way. It works once, and ' +
      'only for a few minutes. You can read it on your phone and type it here.',
    submit: 'Sign in',
  },
  invite: {
    or: 'Or, if that address is not a Google account:',
    hint: 'The address your invitation was sent to. We send a code to prove it is yours.',
    sent:
      'If that is the address your invitation was sent to, a six-digit code is on its way. It ' +
      'works once, and only for a few minutes. You can read it on your phone and type it here.',
    submit: 'Continue',
  },
  claim: {
    or: 'Or, if that address is not a Google account:',
    hint: 'The address the sign-up link was sent to. We send a code to prove it is yours.',
    sent:
      'If that is the address the sign-up link was sent to, a six-digit code is on its way. It ' +
      'works once, and only for a few minutes. You can read it on your phone and type it here.',
    submit: 'Continue',
  },
};

/**
 * The single sign-in surface, shared by all three entry points.
 *
 * One screen rather than three because the difference is a sentence of copy and
 * which endpoint the credential goes to — duplicating the Google plumbing three
 * times would mean three places to get the security-relevant part wrong.
 *
 * **Two doors since TROOP-141, and all three modes offer both since TROOP-224.** The email door was
 * `signin`-only to begin with, on the reasoning that an invite and a claim each exchange a Google
 * credential for a token already bound to a person, so there was nothing for a code to carry. That
 * was the epic's whole defect stated as a design note: a family whose address is not a Google one
 * could sign in from TROOP-141 onwards and could still get through neither door that *creates* an
 * account — which is to say, the two doors that population actually needs.
 *
 * **What made the second half safe is TROOP-224's other half, not the code.** Both invitations now
 * bind the address they were sent to, so proving the mailbox is proving you are the invitee. While
 * they were bearer credentials, offering an emailed-code door here would have been a way to claim
 * somebody else's link with an address of your own choosing.
 */
export function SignInScreen({ mode = 'signin', inviteToken, claimToken, onSignedIn }: Props) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [methods, setMethods] = useState<SignInMethods | null>(null);

  const copy = COPY[mode];

  useEffect(() => {
    let cancelled = false;

    async function setUp() {
      try {
        const live = await authApi.methods();
        if (cancelled) return;
        setMethods(live);

        // The API is the authority on what is live. Rendering a control it would
        // refuse offers something that cannot work — and with two methods the
        // question is whether *neither* is configured, not whether Google is.
        if (!live.google && !live.magicLink) {
          setUnavailable('No sign-in method is configured on this server yet.');
          return;
        }

        if (!live.google || !live.googleClientId || !buttonRef.current) return;

        await renderGoogleButton(
          buttonRef.current,
          live.googleClientId,
          (credential) => void submit(credential),
          copy.button,
        );
      } catch (e) {
        if (!cancelled) {
          setUnavailable(e instanceof Error ? e.message : 'Sign-in is unavailable right now.');
        }
      }
    }

    async function submit(credential: string) {
      setBusy(true);
      setError(null);
      try {
        if (mode === 'invite' && inviteToken) {
          const result = await authApi.acceptInvite(credential, inviteToken);
          onSignedIn({ inviteId: result.inviteId });
        } else if (mode === 'claim' && claimToken) {
          await authApi.claimAccount(credential, claimToken);
          onSignedIn({});
        } else {
          await authApi.signInWithGoogle(credential);
          onSignedIn({});
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Sign-in failed.');
      } finally {
        setBusy(false);
      }
    }

    void setUp();
    return () => {
      cancelled = true;
    };
  }, [mode, inviteToken, claimToken, onSignedIn, copy.button]);

  // Offered wherever there is something for the code to prove *against* — the caller's roster row
  // in `signin`, and the invitation's own bound address in the other two (TROOP-224). Gated on the
  // token actually being present rather than on the mode alone: /start and /claim both bounce a
  // caller who arrived without one, but a form whose submit had nowhere to go would be a control
  // with nothing behind it if that ever stopped being true.
  const emailDoor =
    methods?.magicLink === true &&
    (mode === 'signin' || (mode === 'invite' && !!inviteToken) || (mode === 'claim' && !!claimToken));

  // Only for somebody who arrived without an invitation. An invite and a claim link are each
  // already the answer to "how do we get in", so offering the way to ask there would read as the
  // link they followed not having worked. See {@link BetaNotice}.
  const askingIn = mode === 'signin';

  return (
    <main
      className="flex min-h-screen flex-col items-center"
      style={{ background: 'var(--color-surface-page)', padding: 'var(--space-7) var(--gutter)' }}
    >
      <div className="w-full" style={{ maxWidth: '26rem' }}>
        <Lockup size={30} />

        <h1
          className="t-heading"
          style={{
            color: 'var(--color-text-primary)',
            margin: 'var(--space-6) 0 var(--space-3)',
            textWrap: 'balance',
          }}
        >
          {copy.heading}
        </h1>

        <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {copy.body}
        </p>

        {/* Reserved for Google's button, and only when there is going to be one. The slot is
            44px of held space, so leaving it mounted where Google is off drew a visible gap above
            the email form with nothing in it -- which reads as a control that failed to load. Not
            gated on `methods` being present: it must be mounted on the first pass for
            renderGoogleButton to find the ref, and `undefined !== false` keeps it there until the
            server has actually said no. */}
        {(unavailable || methods?.google !== false) && (
        <div style={{ marginTop: 'var(--space-6)', minHeight: '44px' }}>
          {unavailable ? (
            <p
              className="t-body"
              role="alert"
              style={{
                background: 'var(--color-status-blocked-surface)',
                border: '1px solid var(--color-status-blocked)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-3)',
                color: 'var(--color-text-primary)',
                margin: 0,
              }}
            >
              {unavailable}
            </p>
          ) : (
            // Google's own button, by their terms and because it is the control
            // people recognise. The one place the design system's Button is not used.
            <div ref={buttonRef} aria-busy={busy} />
          )}
        </div>
        )}

        {emailDoor && !unavailable && (
          <MagicLinkForm
            mode={mode}
            inviteToken={inviteToken}
            claimToken={claimToken}
            googleAlso={methods?.google === true}
            onSignedIn={onSignedIn}
          />
        )}

        {error && (
          <p
            className="t-body"
            role="alert"
            style={{
              marginTop: 'var(--space-4)',
              background: 'var(--color-status-blocked-surface)',
              border: '1px solid var(--color-status-blocked)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-3)',
              color: 'var(--color-text-primary)',
            }}
          >
            {error}
          </p>
        )}

        {/* Below the sign-in controls, because somebody on a roster is here to sign in and
            everything above is for them. For a stranger the controls above are worse than nothing:
            `POST /auth/request-link` answers 202 for an address on no roster and sends nothing, so
            a prospective unit types their address, reads `MagicLinkForm`'s deliberately hedged
            "if that address is on a unit roster", and then waits on a link that was never sent.
            That hedge is right and cannot be improved — the endpoint must not answer "is this
            family in this troop" — so the way out is a second door rather than better wording, and
            this is it. It renders even when `unavailable` is set: a server with no sign-in method
            configured is exactly when the only working path on the page is this one. */}
        {askingIn && (
          <div style={{ marginTop: 'var(--space-8)' }}>
            <BetaNotice />
          </div>
        )}

        {/* §8: the disclaimer appears on the sign-up screen, not only in footers. */}
        <p
          className="t-caption"
          style={{
            color: 'var(--color-text-secondary)',
            marginTop: 'var(--space-8)',
            paddingTop: 'var(--space-4)',
            borderTop: '1px solid var(--color-border-subtle)',
          }}
        >
          {DISCLAIMER}
        </p>
      </div>
    </main>
  );
}

/**
 * Ask for a code by email, then type it back in. Two steps on one screen.
 *
 * **The second step is why this is a code and not a link (TROOP-222).** A parent at the laptop
 * reads mail on their phone. A link signs them in on whichever device opened it — the wrong one —
 * and the only way back is forwarding the message to themselves. Six digits are read on one device
 * and typed on the other, which is the everyday case rather than the edge one.
 *
 * **Step one reports the same thing whatever happened, and that is the feature rather than lazy
 * error handling.** `POST /auth/request-link` answers 202 for an address on no roster and sends
 * nothing, so that it cannot be used to ask "is this family in this troop" — and a screen that said
 * *"we don't know that address"* would hand back exactly the answer the endpoint withholds. The
 * server treats a provider outage the same way for the same reason.
 *
 * **Step two is the opposite, and the distinction is worth keeping straight.** Presenting a wrong
 * code is the caller's own failure, so it is reported: without that, somebody who mistyped a digit
 * would sit on a screen that had simply stopped responding. What the server says is still only
 * *that code did not work* — never which of expired, already used, mistyped too often, or never
 * issued at all, because the last of those is the roster question again.
 *
 * **Step one is the same call in all three modes and step two is not (TROOP-224).** Asking for a
 * code knows nothing about invitations — `SignInService.ProveAddressAsync` proves an address and
 * stops there, which is exactly why the invite and claim doors can share it. What differs is what
 * the proof is spent on, and that is one `authApi` call chosen in `verify` rather than a second
 * copy of this form per door.
 */
function MagicLinkForm({
  mode,
  inviteToken,
  claimToken,
  googleAlso,
  onSignedIn,
}: {
  mode: Mode;
  inviteToken?: string;
  claimToken?: string;
  googleAlso: boolean;
  onSignedIn: (result: { inviteId?: string; redirectPath?: string }) => void;
}) {
  const copy = CODE_COPY[mode];
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Where the caller was headed, if they arrived at /sign-in?next=/some/path. Sent when the code
  // is requested rather than when it is verified, because it is stored on the token: that is what
  // makes it survive the parent reading the code on a different device, and what lets the server
  // sanitise it once (MagicLinkToken.SanitizeRedirect) instead of trusting it on the way back.
  //
  // Nothing in the app links here with a `next` yet -- the route guards send a bare /sign-in. This
  // is wired anyway rather than left as dead plumbing behind a comment claiming it works: the path
  // is reachable by hand today (/sign-in?next=/settings), which is the difference between a
  // capability and a constant that guarantees one never fires.
  const [params] = useSearchParams();
  const next = params.get('next');

  const request = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (sending || !email.trim()) return;

      setSending(true);
      setError(null);
      try {
        await authApi.requestLink(email.trim(), next);
        setSent(true);
      } catch (err) {
        // A rate limit is the one failure worth reporting here, and it is not an exception to the
        // silence rule -- it is a fact about this caller rather than about the roster, so saying
        // it reveals nothing the endpoint withholds. Staying quiet would advance the screen to
        // "Check your email" for a code that was never sent, and leave a family waiting on a
        // message that is not coming.
        if (err instanceof ApiError && err.status === 429) {
          setError(err.message);
          return;
        }

        // Everything else is deliberately swallowed. See this component's remarks: the caller is
        // told the same thing either way, and a network failure here is indistinguishable to the
        // person reading the screen from a code that is on its way.
        setSent(true);
      } finally {
        setSending(false);
      }
    },
    [email, next, sending],
  );

  const verify = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (sending || !code.trim()) return;

      setSending(true);
      setError(null);
      try {
        // Three doors, one code. Each spends the proof on the thing the caller arrived holding;
        // the server refuses a code proving an address the invitation was not sent to, and says so
        // in the sentence caught below.
        if (mode === 'invite' && inviteToken) {
          const result = await authApi.acceptInviteByEmail(email.trim(), code.trim(), inviteToken);
          onSignedIn({ inviteId: result.inviteId });
        } else if (mode === 'claim' && claimToken) {
          await authApi.claimAccountByEmail(email.trim(), code.trim(), claimToken);
          onSignedIn({});
        } else {
          const result = await authApi.verifyCode(email.trim(), code.trim());
          onSignedIn({ redirectPath: result.redirectPath });
        }
      } catch (err) {
        // Reported, unlike step one. The server's own sentence, so this screen holds no second
        // copy of a rule about what may be revealed -- including the 429, which says only that
        // there have been too many attempts from here.
        setError(err instanceof Error ? err.message : 'That code did not work.');
      } finally {
        setSending(false);
      }
    },
    [code, email, sending, onSignedIn, mode, inviteToken, claimToken],
  );

  if (sent) {
    return (
      <form onSubmit={verify} style={{ marginTop: 'var(--space-6)' }}>
        <div
          style={{
            padding: 'var(--space-4)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border-subtle)',
            background: 'var(--color-surface-raised)',
          }}
        >
          <p
            className="t-body-strong"
            role="status"
            style={{ color: 'var(--color-text-primary)', margin: 0 }}
          >
            Check your email
          </p>
          <p
            className="t-caption"
            style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-2) 0 var(--space-4)' }}
          >
            {/* No number here on purpose. MagicLinkToken.Lifetime is the one definition and the
                message itself already states it; a "15 minutes" typed on this screen is a second
                copy that goes quietly wrong the day the constant moves. Per mode since TROOP-224 —
                see CODE_COPY for why the hedge is not the same sentence on all three. */}
            {copy.sent}
          </p>

          <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
            <TextField
              label="Your code"
              // Not type="number": it strips leading zeros, and one code in ten begins with one.
              // Spinner arrows on a six-digit code are meaningless besides.
              type="text"
              required
              autoFocus
              // The one autocomplete token a phone will offer a code from a message for.
              autoComplete="one-time-code"
              inputMode="numeric"
              // Room for the spaces somebody will paste with it; the server strips them.
              maxLength={12}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              error={error ?? undefined}
            />

            <Button type="submit" fullWidth variant="primary">
              {sending ? 'Checking…' : copy.submit}
            </Button>
          </div>
        </div>

        {/* A real control rather than "didn't get it? check your spam": asking again is the only
            thing that actually helps, and it is the same door with the address already filled in.
            Sending a second code invalidates the first, which is why the label does not say
            "resend" -- see the reasoning on the claim link's own button. */}
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Button
            type="button"
            fullWidth
            variant="secondary"
            onClick={() => {
              setSent(false);
              setCode('');
              setError(null);
            }}
          >
            Use a different address, or send a new code
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={request} style={{ marginTop: 'var(--space-6)' }}>
      {googleAlso && (
        <p
          className="t-caption"
          style={{
            color: 'var(--color-text-secondary)',
            margin: '0 0 var(--space-4)',
            paddingTop: 'var(--space-4)',
            borderTop: '1px solid var(--color-border-subtle)',
          }}
        >
          {copy.or}
        </p>
      )}

      <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
        <TextField
          label="Email address"
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          hint={copy.hint}
          // Only ever set by a rate limit here; everything else leaves this step silent on
          // purpose. See `request` above.
          error={error ?? undefined}
        />

        <Button type="submit" fullWidth variant={googleAlso ? 'secondary' : 'primary'}>
          {sending ? 'Sending…' : 'Email me a sign-in code'}
        </Button>
      </div>
    </form>
  );
}
